'use client';

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import useCollection from '@/hooks/useCollection';
import { CollectionCard } from '@/components/content-types/collection-type/components/collectionCard';
import { BlogCardSkeleton } from './BlogCardSkeleton';
import { BlogFilters } from './BlogFilters';
import NoResults from '@/components/shared/NoResults';

const LIMIT = 12;
const SKELETON_COUNT = 12;
const SCROLL_SKELETON_COUNT = 3;

/**
 * When the Content Search API returns Blog posts without resolving the `author`
 * relationship (e.g. returns identifier strings instead of full objects), look the
 * contributor up in the pre-fetched contributors map and return the full object.
 */
function resolveAuthor(rawAuthor, contributorMap) {
    if (!rawAuthor) return rawAuthor;

    const entry = Array.isArray(rawAuthor) ? rawAuthor[0] : rawAuthor;
    if (!entry) return rawAuthor;

    // Already a fully-hydrated contributor object
    if (typeof entry === 'object' && (entry.firstName || entry.name)) return rawAuthor;

    // Identifier string or a bare object with only identifier/inode
    const id = typeof entry === 'string' ? entry : entry.identifier || entry.inode;
    if (!id) return rawAuthor;

    const contributor = contributorMap.get(id);
    if (!contributor) return rawAuthor;

    return Array.isArray(rawAuthor) ? [contributor] : contributor;
}

export function BlogList({
    initialCollection,
    contributors = [],
    categories = [],
    deviceType = 'desktop'
}) {
    const [searchValue, setSearchValue] = useState('');
    const [activeContributors, setActiveContributors] = useState([]);
    const [activeCategories, setActiveCategories] = useState([]);
    const [sortOrder, setSortOrder] = useState('desc');

    const loaderRef = useRef(null);

    const queryConfig = useMemo(
        () => ({
            sortBy: { field: 'Blog.postingDate', order: sortOrder },
            defaultQuery: '',
            searchQuery: ''
        }),
        [sortOrder]
    );

    const buildFilter = useCallback(() => {
        const filter = {};

        if (activeContributors.length > 0) {
            filter.contributor = {
                value: activeContributors,
                field: 'author'
            };
        }

        if (activeCategories.length > 0) {
            filter.category = {
                value: activeCategories,
                field: 'categories'
            };
        }

        return filter;
    }, [activeContributors, activeCategories]);

    const { collection, setFilter, setSearch, setPage, isLoading, page } = useCollection({
        limit: LIMIT,
        search: '',
        filter: buildFilter(),
        contentType: 'Blog',
        queryConfig,
        initialCollection,
        depth: 1
    });

    const safeContentlets = useMemo(
        () => collection?.contentlets ?? [],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [collection?.contentlets]
    );
    const safeTotal = collection?.total ?? 0;

    // Index contributors by identifier and inode for O(1) author lookups
    const contributorMap = useMemo(() => {
        const map = new Map();
        contributors.forEach((c) => {
            if (c.identifier) map.set(c.identifier, c);
            if (c.inode) map.set(c.inode, c);
        });
        return map;
    }, [contributors]);

    // Ensure author is always a full object, even when the API returns bare identifiers
    const enrichedContentlets = useMemo(() => {
        if (!contributorMap.size) return safeContentlets;
        return safeContentlets.map((contentlet) => {
            if (!contentlet.author) return contentlet;
            const enriched = resolveAuthor(contentlet.author, contributorMap);
            return enriched === contentlet.author
                ? contentlet
                : { ...contentlet, author: enriched };
        });
    }, [safeContentlets, contributorMap]);

    const handleObserver = useCallback(
        (entries) => {
            const target = entries[0];
            if (target.isIntersecting && safeTotal > safeContentlets.length) {
                setPage((prevPage) => prevPage + 1);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [safeContentlets.length, safeTotal]
    );

    useEffect(() => {
        const option = { root: null, rootMargin: '20px', threshold: 0.1 };
        const observer = new IntersectionObserver(handleObserver, option);
        const currentLoaderRef = loaderRef.current;

        if (currentLoaderRef) observer.observe(currentLoaderRef);

        return () => {
            if (currentLoaderRef) observer.unobserve(currentLoaderRef);
        };
    }, [handleObserver]);

    const handleSearchChange = useCallback(
        (value) => {
            setSearchValue(value);
            setSearch(value);
        },
        [setSearch]
    );

    const handleContributorChange = useCallback(
        (ids) => {
            setActiveContributors(ids);
            setFilter((prev) => ({
                ...prev,
                contributor: {
                    value: ids.length === 0 ? null : ids,
                    field: 'author'
                }
            }));
        },
        [setFilter]
    );

    const handleCategoryChange = useCallback(
        (keys) => {
            setActiveCategories(keys);
            setFilter((prev) => ({
                ...prev,
                category: {
                    value: keys.length === 0 ? null : keys,
                    field: 'categories'
                }
            }));
        },
        [setFilter]
    );

    const handleSortChange = useCallback(
        (order) => {
            setSortOrder(order);
            setPage(1);
        },
        [setPage]
    );

    const handleClear = useCallback(() => {
        setSearchValue('');
        setActiveContributors([]);
        setActiveCategories([]);
        setSortOrder('desc');
        setSearch('');
        setFilter({});
    }, [setSearch, setFilter]);

    const isInitialLoad = isLoading && safeContentlets.length === 0;
    const isResettingLoad = isLoading && page === 1 && safeContentlets.length > 0;
    const isLoadingMore = isLoading && page > 1;

    return (
        <section className="container mx-auto px-4 pb-16 pt-14">
            <h2 className="mb-6 text-3xl font-bold text-blue-700 dark:text-white">
                Browse All Articles
            </h2>
            <BlogFilters
                searchValue={searchValue}
                onSearchChange={handleSearchChange}
                contributors={contributors}
                activeContributors={activeContributors}
                onContributorChange={handleContributorChange}
                categories={categories}
                activeCategories={activeCategories}
                onCategoryChange={handleCategoryChange}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}
                onClear={handleClear}
            />

            <div className="mt-8">
                {isInitialLoad || isResettingLoad ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                            <BlogCardSkeleton key={i} />
                        ))}
                    </div>
                ) : enrichedContentlets.length > 0 ? (
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {enrichedContentlets.map((contentlet) => (
                            <CollectionCard
                                key={contentlet.identifier}
                                contentlet={contentlet}
                                contenttype="Blog"
                                showCategory
                                showAuthor
                                deviceType={deviceType}
                            />
                        ))}
                    </div>
                ) : (
                    <NoResults className="mt-3 w-full" />
                )}

                {isLoadingMore && (
                    <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: SCROLL_SKELETON_COUNT }).map((_, i) => (
                            <BlogCardSkeleton key={`scroll-skeleton-${i}`} />
                        ))}
                    </div>
                )}

                {safeTotal > safeContentlets.length && (
                    <div className="mt-16 flex items-center justify-center">
                        <div ref={loaderRef} className="px-6 py-4">
                            <span />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

export default BlogList;
